import { Badge } from "@designs-by-maryam/ui/components/badge";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Award, Briefcase, GraduationCap, Heart } from "lucide-react";
import { motion } from "motion/react";
import { getStaggerDelay, useSafeMotion } from "@/lib/motion";

type AboutHeroProps = {
	name: string;
	subtitle: string;
	photoSrc: string;
	photoAlt: string;
	bio: string;
	className?: string;
};

const stats = [
	{ value: "5+", label: "Years Experience" },
	{ value: "50+", label: "Projects Completed" },
	{ value: "100%", label: "Client Satisfaction" },
];

const differentiators = [
	{
		icon: GraduationCap,
		title: "Top 10 Graduate",
		description: "Graduated in the top 10 of interior design class",
	},
	{
		icon: Heart,
		title: "Cultural Sensitivity",
		description: "Deep understanding of Bahraini and GCC design preferences",
	},
	{
		icon: Briefcase,
		title: "Comprehensive Service",
		description: "End-to-end design from concept to execution",
	},
];

const expertise = [
	"3D Visualization",
	"AutoCAD",
	"Space Planning",
	"Project Management",
	"Material Selection",
	"Lighting Design",
];

/**
 * AboutHero Component
 *
 * Hero section for the About page featuring professional photo,
 * name, subtitle, biography, stats, differentiators, and expertise.
 *
 * Features:
 * - Responsive layout (side-by-side desktop, stacked mobile)
 * - Motion entrance animations
 * - Edison quote block
 * - Stats cards grid
 * - "What Makes Me Different" section
 * - Core expertise badges
 */
export function AboutHero({
	name,
	subtitle,
	photoSrc,
	photoAlt,
	bio,
	className,
}: AboutHeroProps) {
	const {
		shouldReduce,
		slideRight,
		slideLeft,
		slideUp,
		scaleIn,
		hoverScaleLarge,
	} = useSafeMotion();
	const heroImage = (
		// biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError handles image fallback.
		<img
			alt={photoAlt}
			className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
			height={500}
			loading="eager"
			onError={(e) => {
				e.currentTarget.style.display = "none";
			}}
			src={photoSrc}
			width={400}
		/>
	);

	return (
		<section
			aria-labelledby="about-hero-heading"
			className={cn(
				"relative overflow-hidden bg-background py-12 md:py-16 lg:py-24",
				className,
			)}
		>
			{/* Background Grid Pattern */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.03]"
				style={{
					backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
					backgroundSize: "32px 32px",
				}}
			/>

			<div className="container relative mx-auto px-4 md:px-6">
				<div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
					{/* Photo Section */}
					<motion.div
						animate={slideRight.animate}
						className="relative mx-auto w-full max-w-[400px] lg:sticky lg:top-24 lg:mx-0"
						initial={slideRight.initial}
						transition={slideRight.transition}
					>
						{/* Decorative Background Shape */}
						<motion.div
							animate={scaleIn.animate}
							aria-hidden="true"
							className="-translate-x-4 -z-10 sm:-translate-x-6 absolute inset-0 translate-y-4 rounded-tl-[80px] rounded-br-[40px] bg-primary/10 sm:translate-y-6 lg:rounded-tl-[100px] lg:rounded-br-[50px] dark:bg-primary/5"
							initial={scaleIn.initial}
							transition={{
								duration: shouldReduce ? 0.01 : 0.8,
								delay: shouldReduce ? 0 : 0.2,
							}}
						/>

						{/* Main Image with credential overlay */}
						<div className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted shadow-xl ring-1 ring-border">
							{heroImage}
							{/* Credential overlay on hover */}
							<div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
								<div className="w-full p-6 text-white">
									<div className="mb-2 flex items-center gap-2">
										<Award className="h-5 w-5 text-primary" />
										<span className="font-semibold">CRPEP Licensed</span>
									</div>
									<p className="text-sm text-white/80">
										Category B Engineering License
									</p>
								</div>
							</div>
						</div>

						{/* Decorative Accent Circle */}
						<motion.div
							aria-hidden="true"
							className="-top-4 -right-4 absolute h-16 w-16 rounded-full bg-primary/20 blur-xl dark:bg-primary/10"
							whileHover={hoverScaleLarge}
						/>
					</motion.div>

					{/* Content Section */}
					<motion.div
						animate={slideLeft.animate}
						className="flex flex-col items-center gap-8 text-center lg:items-start lg:text-left"
						initial={slideLeft.initial}
						transition={{
							...slideLeft.transition,
							delay: shouldReduce ? 0 : 0.2,
						}}
					>
						{/* Headlines */}
						<div className="space-y-3">
							<motion.h1
								animate={slideUp.animate}
								className="font-bold text-4xl text-foreground tracking-tight md:text-5xl lg:text-6xl"
								id="about-hero-heading"
								initial={slideUp.initial}
								transition={{
									...slideUp.transition,
									delay: shouldReduce ? 0 : 0.3,
								}}
							>
								{name}
							</motion.h1>
							<motion.p
								animate={slideUp.animate}
								className="font-medium text-primary/80 text-xl md:text-2xl"
								initial={slideUp.initial}
								transition={{
									...slideUp.transition,
									delay: shouldReduce ? 0 : 0.4,
								}}
							>
								{subtitle}
							</motion.p>
						</div>

						{/* Bio */}
						<motion.p
							animate={{ opacity: 1 }}
							className="max-w-[600px] text-lg text-muted-foreground leading-relaxed"
							initial={{ opacity: 0 }}
							transition={{
								duration: shouldReduce ? 0.01 : 0.5,
								delay: shouldReduce ? 0 : 0.5,
							}}
						>
							{bio}
						</motion.p>

						{/* Edison Quote Block - Standardized styling matching home page */}
						<motion.div
							animate={slideUp.animate}
							className="rounded-2xl border border-primary/20 bg-primary/5 p-6 dark:border-primary/30 dark:bg-primary/10"
							initial={slideUp.initial}
							transition={{
								...slideUp.transition,
								delay: shouldReduce ? 0 : 0.6,
							}}
						>
							<blockquote className="mb-3 text-lg text-muted-foreground italic">
								"If we did all the things we are capable of we would literally
								astound ourselves."
							</blockquote>
							<cite className="font-medium text-primary text-sm">
								— Thomas Edison
							</cite>
						</motion.div>

						{/* Stats Grid */}
						<motion.div
							animate={slideUp.animate}
							className="relative grid w-full grid-cols-3 gap-6 rounded-lg border border-border/30 bg-muted/20 p-6 shadow-sm backdrop-blur-sm"
							initial={slideUp.initial}
							transition={{
								...slideUp.transition,
								delay: shouldReduce ? 0 : 0.7,
							}}
						>
							{/* Decorative gradient overlay */}
							<div
								aria-hidden="true"
								className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-primary/5 to-purple-500/5"
							/>

							{stats.map((stat, index) => (
								<motion.div
									animate={scaleIn.animate}
									className="group relative flex flex-col items-center lg:items-start"
									initial={scaleIn.initial}
									key={stat.label}
									transition={{
										duration: shouldReduce ? 0.01 : 0.3,
										delay: getStaggerDelay(index, 0.8, 0.1, shouldReduce),
									}}
								>
									<span className="relative bg-gradient-to-br from-primary to-primary/80 bg-clip-text font-bold text-2xl text-foreground text-transparent md:text-3xl">
										{stat.value}
									</span>
									<span className="text-muted-foreground text-xs md:text-sm">
										{stat.label}
									</span>
									{/* Separator line (not for last item) */}
									{index < stats.length - 1 && (
										<div
											aria-hidden="true"
											className="-right-3 -translate-y-1/2 absolute top-1/2 hidden h-8 w-px bg-border/50 lg:block"
										/>
									)}
								</motion.div>
							))}
						</motion.div>

						{/* What Makes Me Different */}
						<motion.div
							animate={slideUp.animate}
							className="w-full space-y-5"
							initial={slideUp.initial}
							transition={{
								...slideUp.transition,
								delay: shouldReduce ? 0 : 0.9,
							}}
						>
							<h2 className="font-semibold text-foreground text-lg">
								What Makes Me Different
							</h2>
							<div className="grid gap-4 sm:grid-cols-3">
								{differentiators.map((item, index) => (
									<motion.div
										animate={slideUp.animate}
										className="group hover:-translate-y-1 relative flex flex-col items-center gap-3 overflow-hidden rounded-xl border border-border/30 bg-card/50 p-5 text-center shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-primary/30 hover:shadow-md lg:items-start lg:text-left dark:hover:border-primary/40"
										initial={slideUp.initial}
										key={item.title}
										transition={{
											duration: shouldReduce ? 0.01 : 0.3,
											delay: getStaggerDelay(index, 1.0, 0.1, shouldReduce),
										}}
									>
										{/* Gradient overlay */}
										<div
											aria-hidden="true"
											className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 opacity-0 transition-opacity duration-300 group-hover:from-primary/5 group-hover:to-purple-500/5 group-hover:opacity-100"
										/>

										{/* Icon with background */}
										<motion.div
											className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 text-primary shadow-sm transition-transform duration-300 group-hover:scale-110 dark:from-primary/20 dark:to-primary/10 dark:text-primary"
											whileHover={{ rotate: 5 }}
										>
											<item.icon className="relative z-10 h-5 w-5" />
											{/* Icon glow */}
											<div className="-z-10 absolute inset-0 rounded-lg bg-primary/20 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100" />
										</motion.div>

										<div className="relative space-y-1">
											<h3 className="font-semibold text-foreground text-sm leading-tight">
												{item.title}
											</h3>
											<p className="text-muted-foreground text-xs leading-relaxed">
												{item.description}
											</p>
										</div>

										{/* Bottom accent */}
										<div
											aria-hidden="true"
											className="absolute inset-x-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-primary to-primary/80 transition-all duration-300 group-hover:w-full"
										/>
									</motion.div>
								))}
							</div>
						</motion.div>

						{/* Core Expertise Badges */}
						<motion.div
							animate={slideUp.animate}
							className="w-full space-y-4"
							initial={slideUp.initial}
							transition={{
								...slideUp.transition,
								delay: shouldReduce ? 0 : 1.1,
							}}
						>
							<h2 className="font-semibold text-foreground text-lg">
								Core Expertise
							</h2>
							<div className="flex flex-wrap gap-2.5">
								{expertise.map((skill, index) => (
									<motion.div
										animate={scaleIn.animate}
										initial={scaleIn.initial}
										key={skill}
										transition={{
											duration: shouldReduce ? 0.01 : 0.2,
											delay: getStaggerDelay(index, 1.2, 0.05, shouldReduce),
										}}
										whileHover={{ scale: 1.05 }}
									>
										<Badge
											className="group relative overflow-hidden border border-primary/20 bg-gradient-to-r from-primary/15 to-primary/5 px-3.5 py-1.5 font-medium text-primary shadow-sm transition-all duration-300 hover:border-primary/40 hover:shadow-md dark:border-primary/30 dark:from-primary/20 dark:to-primary/10 dark:text-primary dark:hover:border-primary/50"
											variant="secondary"
										>
											{/* Shimmer effect */}
											<div
												aria-hidden="true"
												className="-translate-x-full absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
											/>
											<span className="relative">{skill}</span>
										</Badge>
									</motion.div>
								))}
							</div>
						</motion.div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
